import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Code2, Smartphone, Database, Cloud, Mail, Github, Linkedin } from 'lucide-react';

const HirePage = () => {
  const skills = [
    'React', 'Node.js', 'Python', 'FastAPI', 'MongoDB', 'PostgreSQL',
    'AWS', 'Docker', 'TypeScript', 'REST APIs', 'GraphQL', 'CI/CD'
  ];

  const services = [
    {
      icon: Code2,
      title: 'Web Development',
      description: 'Full-stack web applications using modern frameworks and technologies',
      features: ['Custom web apps', 'API development', 'Database design', 'Cloud deployment']
    },
    {
      icon: Smartphone,
      title: 'Tool Development',
      description: 'Custom developer tools and utilities tailored to your needs',
      features: ['CLI tools', 'Browser extensions', 'Automation scripts', 'Dev utilities']
    },
    {
      icon: Database,
      title: 'Backend Systems',
      description: 'Scalable backend systems with robust APIs and databases',
      features: ['RESTful APIs', 'Real-time systems', 'Database optimization', 'Microservices']
    },
    {
      icon: Cloud,
      title: 'Cloud Solutions',
      description: 'Cloud infrastructure and deployment solutions',
      features: ['AWS/GCP setup', 'CI/CD pipelines', 'Container orchestration', 'Monitoring']
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-cyan-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-white/20 text-white border-0 text-sm px-4 py-1">
              Available for Hire
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold">
              Let's Build Something Amazing Together
            </h1>
            <p className="text-xl text-blue-100">
              Full-stack developer specializing in modern web applications and developer tools
            </p>
            <Button asChild size="lg" variant="secondary">
              <a href="#contact">Get in Touch</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Skills & Technologies</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {skills.map(skill => (
                <Badge key={skill} variant="secondary" className="text-base px-4 py-2">
                  {skill}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Services I Offer
            </h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Professional development services for your next project
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-gray-200 dark:border-gray-800 hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <div className="p-3 rounded-lg bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 w-fit">
                      <Icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <CardTitle className="mt-4">{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-gray-700 dark:text-gray-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mr-2"></span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              Ready to Start Your Project?
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg">
              Get in touch to discuss your requirements
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button asChild size="lg">
                <a href="mailto:hello@devtoolshub.com">
                  <Mail className="w-5 h-5 mr-2" />
                  Email Me
                </a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href="https://github.com" target="_blank" rel="noopener noreferrer">
                  <Github className="w-5 h-5 mr-2" />
                  GitHub
                </a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
                  <Linkedin className="w-5 h-5 mr-2" />
                  LinkedIn
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Placement */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-8 text-center border-2 border-dashed border-gray-300 dark:border-gray-700">
              <p className="text-gray-500 dark:text-gray-400 text-sm">Advertisement Space</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HirePage;